import * as React from "react"
import { useState, useRef, useEffect } from "react"
import { Card, CardContent } from "@/components/ui/card"
import { t, Language, isRTL } from "@/lib/i18n"
import { AnimatePresence, motion } from "framer-motion"
import { Link } from "react-router-dom"
import { prefetchScheme } from "@/lib/schemes"

type TabKey = "news" | "schemes" | "projects"

export interface TabbedNewsProps {
  lang: Language
  newsItems?: string[]
  schemeItems?: string[]
  projectItems?: string[]
}

const TAB_BG: Record<TabKey, string> = {
  news: "bg-blue-600 hover:bg-blue-700",
  schemes: "bg-green-600 hover:bg-green-700",
  projects: "bg-amber-500 hover:bg-amber-600",
}

const TAB_INACTIVE_BG: Record<TabKey, string> = {
  news: "bg-blue-100 hover:bg-blue-200",
  schemes: "bg-green-100 hover:bg-green-200",
  projects: "bg-amber-100 hover:bg-amber-200",
}

export function TabbedNews({ lang, newsItems, schemeItems, projectItems }: TabbedNewsProps) {
  const [active, setActive] = useState<TabKey>("news")
  const tabsRef = useRef<Record<TabKey, HTMLButtonElement | null>>({ news: null, schemes: null, projects: null })

  // Normalize helper
  const normalize = (s: string) => s.trim().toLowerCase()

  // Hindi mapping for known scheme names
  const HI_MAP: Record<string, string> = {
    [normalize("Mahatma Gandhi National Rural Employment Guarantee Scheme (MGNREGS)")]: "महात्मा गांधी राष्ट्रीय ग्रामीण रोजगार गारंटी योजना (मनरेगा)",
    [normalize("National Rural Livelihoods Mission (NRLM)/Aajeevika")]: "राष्ट्रीय ग्रामीण आजीविका मिशन (NRLM)/ आजीविका",
    [normalize("Deen Dayal Upadhaya- Gramin Kaushal Yojana (DDU-GKY)")]: "दीनदयाल उपाध्याय ग्रामीण कौशल योजना (DDU-GKY)",
    [normalize("Rural Self Employment Training Institutes (RSETIs)")]: "ग्रामीण स्व-रोजगार प्रशिक्षण संस्थान (RSETI)",
    [normalize("Pradhan Mantri Krishi Sinchayee Yojana erstwhile-Watershed Management Programme (IWMP/ Now PMKSY)")]: "प्रधानन्त्री कृषि सिंचाई योजना (पूर्व: IWMP/ अब PMKSY)",
    [normalize("Member of Parliament Local Area Development Scheme (MPLADS)")]: "सांसद स्थानीय क्षेत्र विकास योजना (MPLADS)",
    [normalize("Vidhayak Adarsh Nagar Avam Gram Yojana (VANGY)")]: "विधायक आदर्श नगर एवं ग्राम योजना (VANGY)",
    [normalize("Pradhan Mantri Adarsh Gram Yojana (PMAGY)")]: "प्रधानमंत्री आदर्श ग्राम योजना (PMAGY)",
    [normalize("Saansad Adarsh Gram Yojana (SAGY)")]: "सांसद आदर्श ग्राम योजना (SAGY)",
    [normalize("Shyama Prasad Mukherji Rurban Mission (SPMRM)")]: "श्यामा प्रसाद मुखर्जी रूर्बन मिशन (SPMRM)",
    [normalize("Swaranjayanti Khand Utthan Yojana (SKUY)")]: "स्वर्णजयंती खंड उत्थान योजना (SKUY)",
    [normalize("Matching Grant Scheme")]: "मिलान अनुदान योजना",
    [normalize("Haryana Rural Development Authority (HRDA)")]: "हरियाणा ग्रामीण विकास प्राधिकरण (HRDA)",
    [normalize("Haryana Swaran Jayanti Mahagram Yojna (SMAGY)")]: "हरियाणा स्वर्ण जयंती महाग्राम योजना (SMAGY)",
    [normalize("Mahatama Gandhi Gramin Basti Yojna Scheme (MGGBYS)")]: "महात्मा गांधी ग्रामीण बस्ती योजना (MGGBYS)",
    [normalize("Haryana Gramin Vikas Yojana (HGVY)")]: "हरियाणा ग्रामीण विकास योजना (HGVY)",
    [normalize("Deenbandhu Gram Uday Yojana Scheme")]: "दीनबंधु ग्राम उदय योजना",
    [normalize("Swachh Bharat Mission-Gramin (SBM-G)")]: "स्वच्छ भारत मिशन-ग्रामीण (SBM-G)",
    [normalize("Pradhan Mantri Jan Vikas Karyakram (PMJVK)")]: "प्रधानमंत्री जन विकास कार्यक्रम (PMJVK)",
    [normalize("Start-up Village Entrepreneurship Programme (SVEP)")]: "स्टार्ट-अप विलेज उद्यमिता कार्यक्रम (SVEP)",
  }

  // Punjabi mapping for known scheme names
  const PA_MAP: Record<string, string> = {
    [normalize("Mahatma Gandhi National Rural Employment Guarantee Scheme (MGNREGS)")]: "ਮਹਾਤਮਾ ਗਾਂਧੀ ਰਾਸ਼ਟਰੀ ਪਿੰਡ ਰੋਜ਼ਗਾਰ ਗਾਰੰਟੀ ਯੋਜਨਾ (MGNREGS)",
    [normalize("National Rural Livelihoods Mission (NRLM)/Aajeevika")]: "ਰਾਸ਼ਟਰੀ ਪਿੰਡ ਆਜੀਵਿਕਾ ਮਿਸ਼ਨ (NRLM)/ ਆਜੀਵਿਕਾ",
    [normalize("Deen Dayal Upadhaya- Gramin Kaushal Yojana (DDU-GKY)")]: "ਦੀਨ ਦਯਾਲ ਉਪਾਧਿਆਏ ਗ੍ਰਾਮੀਣ ਕੌਸ਼ਲ ਯੋਜਨਾ (DDU-GKY)",
    [normalize("Rural Self Employment Training Institutes (RSETIs)")]: "ਗ੍ਰਾਮੀਣ ਸਵ-ਰੋਜ਼ਗਾਰ ਤਾਲੀਮ ਸੰਸਥਾਨ (RSETIs)",
    [normalize("Pradhan Mantri Krishi Sinchayee Yojana erstwhile-Watershed Management Programme (IWMP/ Now PMKSY)")]: "ਪ੍ਰਧਾਨ ਮੰਤਰੀ ਕ੍ਰਿਸ਼ੀ ਸਿੰਚਾਈ ਯੋਜਨਾ (PMKSY) (ਪਹਿਲਾਂ IWMP)",
    [normalize("Member of Parliament Local Area Development Scheme (MPLADS)")]: "ਸਾਂਸਦ ਸਥਾਨਕ ਖੇਤਰ ਵਿਕਾਸ ਯੋਜਨਾ (MPLADS)",
    [normalize("Vidhayak Adarsh Nagar Avam Gram Yojana (VANGY)")]: "ਵਿਧਾਇਕ ਆਦਰਸ਼ ਨਗਰ ਅਤੇ ਗ੍ਰਾਮ ਯੋਜਨਾ (VANGY)",
    [normalize("Pradhan Mantri Adarsh Gram Yojana (PMAGY)")]: "ਪ੍ਰਧਾਨ ਮੰਤਰੀ ਆਦਰਸ਼ ਗ੍ਰਾਮ ਯੋਜਨਾ (PMAGY)",
    [normalize("Saansad Adarsh Gram Yojana (SAGY)")]: "ਸਾਂਸਦ ਆਦਰਸ਼ ਗ੍ਰਾਮ ਯੋਜਨਾ (SAGY)",
    [normalize("Shyama Prasad Mukherji Rurban Mission (SPMRM)")]: "ਸ਼ਿਆਮਾ ਪ੍ਰਸਾਦ ਮੁਖਰਜੀ ਰੂਬਰਬਨ ਮਿਸ਼ਨ (SPMRM)",
    [normalize("Swaranjayanti Khand Utthan Yojana (SKUY)")]: "ਸਵਰਨ ਜਯੰਤੀ ਖੰਡ ਉਤਥਾਨ ਯੋਜਨਾ (SKUY)",
    [normalize("Matching Grant Scheme")]: "ਮੈਚਿੰਗ ਗ੍ਰਾਂਟ ਯੋਜਨਾ",
    [normalize("Haryana Rural Development Authority (HRDA)")]: "ਹਰਿਆਣਾ ਗ੍ਰਾਮੀਣ ਵਿਕਾਸ ਪ੍ਰਾਧਿਕਰਨ (HRDA)",
    [normalize("Haryana Swaran Jayanti Mahagram Yojna (SMAGY)")]: "ਹਰਿਆਣਾ ਸਵਰਨ ਜਯੰਤੀ ਮਹਾਗ੍ਰਾਮ ਯੋਜਨਾ (SMAGY)",
    [normalize("Mahatama Gandhi Gramin Basti Yojna Scheme (MGGBYS)")]: "ਮਹਾਤਮਾ ਗਾਂਧੀ ਗ੍ਰਾਮੀਣ ਬਸਤੀ ਯੋਜਨਾ (MGGBYS)",
    [normalize("Haryana Gramin Vikas Yojana (HGVY)")]: "ਹਰਿਆਣਾ ਗ੍ਰਾਮੀਣ ਵਿਕਾਸ ਯੋਜਨਾ (HGVY)",
    [normalize("Deenbandhu Gram Uday Yojana Scheme")]: "ਦੀਨਬੰਧੂ ਗ੍ਰਾਮ ਉਦਯ ਯੋਜਨਾ",
    [normalize("Swachh Bharat Mission-Gramin (SBM-G)")]: "ਸਵੱਛ ਭਾਰਤ ਮਿਸ਼ਨ-ਗ੍ਰਾਮੀਣ (SBM-G)",
    [normalize("Pradhan Mantri Jan Vikas Karyakram (PMJVK)")]: "ਪ੍ਰਧਾਨ ਮੰਤਰੀ ਜਨ ਵਿਕਾਸ ਕਰਿਆਕਰਮ (PMJVK)",
    [normalize("Start-up Village Entrepreneurship Programme (SVEP)")]: "ਸਟਾਰਟ-ਅਪ ਵਿਲੇਜ ਉਦਯਮਿਤਾ ਪ੍ਰੋਗਰਾਮ (SVEP)",
  }

  // English name to slug mapping
  const EN_TO_SLUG: Record<string, string> = {
    [normalize("Mahatma Gandhi National Rural Employment Guarantee Scheme (MGNREGS)")]: "mgnregs",
    [normalize("National Rural Livelihoods Mission (NRLM)/Aajeevika")]: "nrlm",
    [normalize("Deen Dayal Upadhaya- Gramin Kaushal Yojana (DDU-GKY)")]: "ddu-gky",
    [normalize("Rural Self Employment Training Institutes (RSETIs)")]: "rsetis",
    [normalize("Pradhan Mantri Krishi Sinchayee Yojana erstwhile-Watershed Management Programme (IWMP/ Now PMKSY)")]: "pmksy",
    [normalize("Member of Parliament Local Area Development Scheme (MPLADS)")]: "mplads",
    [normalize("Vidhayak Adarsh Nagar Avam Gram Yojana (VANGY)")]: "vangy",
    [normalize("Pradhan Mantri Adarsh Gram Yojana (PMAGY)")]: "pmagy",
    [normalize("Saansad Adarsh Gram Yojana (SAGY)")]: "sagy",
    [normalize("Shyama Prasad Mukherji Rurban Mission (SPMRM)")]: "spmrm",
    [normalize("Swaranjayanti Khand Utthan Yojana (SKUY)")]: "skuy",
    [normalize("Matching Grant Scheme")]: "matching-grant",
    [normalize("Haryana Rural Development Authority (HRDA)")]: "hrda",
    [normalize("Haryana Swaran Jayanti Mahagram Yojna (SMAGY)")]: "smagy",
    [normalize("Mahatama Gandhi Gramin Basti Yojna Scheme (MGGBYS)")]: "mggbys",
    [normalize("Haryana Gramin Vikas Yojana (HGVY)")]: "hgvy",
    [normalize("Deenbandhu Gram Uday Yojana Scheme")]: "deenbandhu-gram-uday",
    [normalize("Swachh Bharat Mission-Gramin (SBM-G)")]: "sbm-g",
    [normalize("Pradhan Mantri Jan Vikas Karyakram (PMJVK)")]: "pmjvk",
    [normalize("Start-up Village Entrepreneurship Programme (SVEP)")]: "svep",
  }

  // Map Hindi names to the same slugs for robust linking
  const HI_TO_SLUG: Record<string, string> = Object.fromEntries(
    Object.entries(HI_MAP).map(([enNorm, hiName]) => [normalize(hiName), EN_TO_SLUG[enNorm]])
  )

  // Map Punjabi names to the same slugs for robust linking
  const PA_TO_SLUG: Record<string, string> = Object.fromEntries(
    Object.entries(PA_MAP).map(([enNorm, paName]) => [normalize(paName), EN_TO_SLUG[enNorm]])
  )
  const getSlug = (label: string) =>
    EN_TO_SLUG[normalize(label)] ?? HI_TO_SLUG[normalize(label)] ?? PA_TO_SLUG[normalize(label)] ?? "scheme"

  const labels: Record<TabKey, string> = {
    news: t("news_tabs_announcements", lang),
    schemes: t("news_tabs_schemes", lang),
    projects: t("news_tabs_projects", lang),
  }

  const defaults: Record<TabKey, string[]> = {
    news: [
      lang === "hi"
        ? "ग्रामीण हरियाणा में महिला उद्यमियों के लिए नई योजना शुरू।"
        : lang === "pa"
        ? "ਪਿੰਡੀਂ ਹਰਿਆਣਾ ਵਿੱਚ ਔਰਤ ਉਦਯਮੀਆਂ ਲਈ ਨਵੀਂ ਯੋਜਨਾ ਸ਼ੁਰੂ।"
        : "New scheme launched for women entrepreneurs in rural Haryana.",
      lang === "hi"
        ? "जिलों में आगामी ग्राम सभा बैठकों की घोषणा।"
        : lang === "pa"
        ? "ਜ਼ਿਲ੍ਹਿਆਂ ਵਿੱਚ ਆਉਣ ਵਾਲੀਆਂ ਗ੍ਰਾਮ ਸਭਾ ਬੈਠਕਾਂ ਦੀ ਘੋਸ਼ਣਾ।"
        : "Upcoming Gram Sabha meetings across districts announced.",
      lang === "hi"
        ? "अंबाला और कुरुक्षेत्र में बाढ़ राहत परियोजनाएँ शुरू।"
        : lang === "pa"
        ? "ਅੰਬਾਲਾ ਅਤੇ ਕੁਰੁਕਸ਼ੇਤਰ ਵਿੱਚ ਬਾੜ੍ਹ ਰਾਹਤ ਪ੍ਰੋਜੈਕਟ ਸ਼ੁਰੂ।"
        : "Flood relief projects initiated in Ambala and Kurukshetra.",
    ],
    schemes: [
      lang === "hi"
        ? "मुख्यमंत्री ग्रामीण उत्थान योजना के पंजीकरण खुले।"
        : lang === "pa"
        ? "ਮੁੱਖ ਮੰਤਰੀ ਗ੍ਰਾਮੀਣ ਉਤਥਾਨ ਯੋਜਨਾ ਦੇ ਰਜਿਸਟ੍ਰੇਸ਼ਨ ਖੁਲ੍ਹੇ।"
        : "Chief Minister Rural Upliftment Scheme registrations open.",
      lang === "hi"
        ? "प्रधानमंत्री आवास योजना के नए लाभार्थियों की सूची प्रकाशित।"
        : lang === "pa"
        ? "PMAY ਦੇ ਨਵੇਂ ਲਾਭਪਾਤਰੀਆਂ ਦੀ ਸੂਚੀ ਪ੍ਰਕਾਸ਼ਿਤ।"
        : "PMAY new beneficiaries list published.",
      lang === "hi"
        ? "स्वच्छ भारत मिशन के अंतर्गत नई शौचालय सब्सिडी अनुमोदित।"
        : lang === "pa"
        ? "ਸਵੱਛ ਭਾਰਤ ਮਿਸ਼ਨ ਤਹਿਤ ਨਵੇਂ ਟੌਇਲਟ ਸਬਸਿਡੀ ਮਨਜ਼ੂਰ।"
        : "New toilet subsidy approved under Swachh Bharat Mission.",
    ],
    projects: [
      lang === "hi"
        ? "ग्रामीण सड़कों के उन्नयन हेतु चरण-II कार्य आरंभ।"
        : lang === "pa"
        ? "ਗ੍ਰਾਮੀਣ ਸੜਕਾਂ ਅਪਗ੍ਰੇਡ ਲਈ ਫੇਜ਼-II ਕੰਮ ਸ਼ੁਰੂ।"
        : "Phase-II works begin for rural road upgrades.",
      lang === "hi"
        ? "जल संरक्षण तालाब पुनर्जीवन परियोजना 20 ग्रामों में विस्तारित।"
        : lang === "pa"
        ? "ਜਲ ਸੰਰੱਖਣ ਪੌੰਡ ਰੀਵਾਈਵਲ ਪ੍ਰੋਜੈਕਟ 20 ਪਿੰਡਾਂ ਵਿੱਚ ਵਧਾਇਆ।"
        : "Water conservation pond revival expanded to 20 villages.",
      lang === "hi"
        ? "समुदाय केंद्र आधुनिकीकरण परियोजना के निविदाएँ जारी।"
        : lang === "pa"
        ? "ਕਮਿਊਨਿਟੀ ਸੈਂਟਰ ਮੌਡਰਨਾਈਜ਼ੇਸ਼ਨ ਪ੍ਰੋਜੈਕਟ ਲਈ ਟੈਂਡਰ ਜਾਰੀ।"
        : "Tenders released for Community Center modernization project.",
    ],
  }

  const items: Record<TabKey, string[]> = {
    news: newsItems ?? defaults.news,
    schemes: ((): string[] => {
      const source = schemeItems ?? defaults.schemes
      if (lang === "hi") return source.map((s) => HI_MAP[normalize(s)] ?? s)
      if (lang === "pa") return source.map((s) => PA_MAP[normalize(s)] ?? s)
      return source
    })(),
    projects: projectItems ?? defaults.projects,
  }

  // Keyboard navigation for tabs
  const order: TabKey[] = ["news", "schemes", "projects"]
  const onTabKeyDown = (e: React.KeyboardEvent) => {
    const idx = order.indexOf(active)
    if (e.key === "ArrowRight") {
      const next = order[(idx + 1) % order.length]
      setActive(next)
      tabsRef.current[next]?.focus()
      e.preventDefault()
    } else if (e.key === "ArrowLeft") {
      const prev = order[(idx - 1 + order.length) % order.length]
      setActive(prev)
      tabsRef.current[prev]?.focus()
      e.preventDefault()
    } else if (e.key === "Home") {
      setActive(order[0])
      tabsRef.current[order[0]]?.focus()
      e.preventDefault()
    } else if (e.key === "End") {
      setActive(order[order.length - 1])
      tabsRef.current[order[order.length - 1]]?.focus()
      e.preventDefault()
    }
  }

  useEffect(() => {
    // Ensure the active tab is focusable for accessibility
    tabsRef.current[active]?.setAttribute("tabindex", "0")
  }, [active])

  const panelId = (key: TabKey) => `tab-panel-${key}`
  const tabId = (key: TabKey) => `tab-${key}`

  return (
    <Card className="bg-white shadow-lg">
      <CardContent className="p-4">
        <div
          role="tablist"
          aria-label={t("news_title", lang)}
          className="flex flex-wrap gap-2 mb-4"
          onKeyDown={onTabKeyDown}
        >
          {order.map((key) => {
            const isActive = active === key
            const activeStyles = `${TAB_BG[key]} text-white`
            const inactiveStyles = `${TAB_INACTIVE_BG[key]} text-gray-800`
            return (
              <button
                key={key}
                id={tabId(key)}
                role="tab"
                aria-selected={isActive}
                aria-controls={panelId(key)}
                tabIndex={isActive ? 0 : -1}
                ref={(el) => (tabsRef.current[key] = el)}
                className={`px-3 py-1 rounded-md text-[15px] font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-700 transition-colors ${
                  isActive ? activeStyles : inactiveStyles
                }`}
                onClick={() => setActive(key)}
              >
                {labels[key]}
              </button>
            )
          })}
        </div>

        <div className="min-h-[220px] md:min-h-[320px]" aria-live="polite">
          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              role="tabpanel"
              id={panelId(active)}
              aria-labelledby={tabId(active)}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.18 }}
              className="w-full"
            >
              <ul
                className="space-y-2 text-gray-700"
                aria-label={labels[active]}
                lang={lang}
                dir={isRTL(lang) ? "rtl" : "ltr"}
              >
                {items[active].map((line, i) => {
                  if (active === "schemes") {
                    const slug = getSlug(line)
                    const aria =
                      lang === "hi"
                        ? `${line} योजना का विवरण खोलें`
                        : lang === "pa"
                        ? `${line} ਦਾ ਵੇਰਵਾ ਖੋਲ੍ਹੋ`
                        : `Open ${line} details`
                    return (
                      <li key={i} className="leading-relaxed">
                        •{" "}
                        <Link
                          to={`/schemes/${slug}?lang=${lang}`}
                          aria-label={aria}
                          className="text-inherit hover:text-green-700 hover:underline focus:outline-none focus:ring-2 focus:ring-green-600 rounded-sm"
                          onMouseEnter={() => prefetchScheme(slug, lang)}
                          onFocus={() => prefetchScheme(slug, lang)}
                          onClick={() => prefetchScheme(slug, lang)}
                        >
                          {line}
                        </Link>
                      </li>
                    )
                  }
                  return (
                    <li key={i} className="leading-relaxed">• {line}</li>
                  )
                })}
              </ul>
            </motion.div>
          </AnimatePresence>
        </div>
      </CardContent>
    </Card>
  )
}